import { useContext, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { GeneralQueryKeys, Navigation } from "../../../api/constants"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"
import { useAbility } from "../../../components/contexts/AbilityContext"
import Loading from "../../../components/common/Loading"
import { ModalContext } from "../../../components/contexts/ModalContext"
import BaseButton from "../../../components/elements/BaseButton"
import BaseButtonRow from "../../../components/elements/BaseButtonRow"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BasePage from "../../../components/layout/BasePage"
import ErrorPage from "../../404/ErrorPage"
import { useDelayWhenSearchWithPagination } from "../../../services/functions"
import { getEmployeesForTourAssignment, getTour, getTourList, getTourTasks } from "../api/apiCalls"
import { groupTasksByObject } from "../data/tourTasks"
import {
    useAddTourStopMutation,
    useMoveTourStopToTourMutation,
    useMoveTourStopWeekdayMutation,
    useRemoveTourStopMutation,
    useReorderTourStopMutation,
    useUpdateTourMutation,
} from "../api/services"
import { ReactComponent as BACKARROWLEFT } from "../../../assets/BackArrowLeft.svg"
import { ReactComponent as HOUSE } from "../../../assets/HouseICON.svg"
import { ReactComponent as PEOPLE } from "../../../assets/People.svg"
import AddStopModal from "../components/AddStopModal"
import EditTourModal from "../components/EditTourModal"
import TourWeekBoard from "../components/TourWeekBoard"
import { TourWeekday } from "../types"
import { SquarePen } from "lucide-react"

export default function TourDetailPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const ability = useAbility()
    const canEdit = ability.can(P.termine_management.action, P.termine_management.subject)
    const modalCon = useContext(ModalContext)
    const queryClient = useQueryClient()
    const [addWeekday, setAddWeekday] = useState<TourWeekday>(1)
    const [searchEmployee, setSearchEmployee] = useState("")
    const searchEmployeeParams = useDelayWhenSearchWithPagination(searchEmployee)

    const { data: tour, isLoading, error } = useQuery({
        queryKey: [GeneralQueryKeys.TOUR_SINGLE + (id ?? "")],
        queryFn: () => getTour(id ?? ""),
        enabled: !!id,
    })
    const { data: allTours } = useQuery({
        queryKey: [GeneralQueryKeys.TOURS],
        queryFn: getTourList,
    })
    const { data: employees, isLoading: employeesLoading } = useQuery({
        queryKey: [GeneralQueryKeys.EMPLOYEE_NAMES_ONLY + "tour" + searchEmployeeParams],
        queryFn: () => getEmployeesForTourAssignment(searchEmployeeParams),
    })
    const { data: weekTasks } = useQuery({
        queryKey: [GeneralQueryKeys.TOUR_TASKS + "currentWeek"],
        queryFn: () => getTourTasks({ currentWeekOnly: true }),
    })
    const { mutate: updateTour } = useUpdateTourMutation(queryClient)
    const { mutate: addStop } = useAddTourStopMutation(queryClient)
    const { mutate: removeStop } = useRemoveTourStopMutation(queryClient)
    const { mutate: reorderStop } = useReorderTourStopMutation(queryClient)
    const { mutate: moveWeekday } = useMoveTourStopWeekdayMutation(queryClient)
    const { mutate: moveToTour } = useMoveTourStopToTourMutation(queryClient)

    const tasksByObject = useMemo(() => groupTasksByObject(weekTasks ?? []), [weekTasks])
    const otherTours = useMemo(() => (allTours ?? []).filter((item) => item._id !== id), [allTours, id])
    const planStops = useMemo(
        () => (tour?.stops.filter((stop) => !stop.isAdHoc).map((stop) => ({ objectId: stop.object._id, weekday: stop.weekday })) ?? []),
        [tour],
    )
    const employeeName = tour?.employee ? `${tour.employee.first_name} ${tour.employee.last_name}` : "Nicht zugewiesen"
    const objectCount = tour?.stops.length ?? 0

    if (isLoading) return <Loading />
    if (error || !tour) return <ErrorPage />

    return (
        <BasePage>
            <BaseHeadBanner title={tour.name} />
            <BaseButtonRow>
                <BaseButton
                    label="Zur Übersicht"
                    icon={<BACKARROWLEFT className="w-5 h-5 shrink-0" />}
                    func={() => navigate(Navigation.TOURS)}
                    variant="ghost"
                    classProps="h-12"
                />
                <div className="flex items-center gap-4 min-w-0">
                    <div className="flex items-center min-w-0">
                        <PEOPLE className="w-5 h-5 shrink-0 text-primary" />
                        <p title={employeeName} className="pl-2 text-sm 2xl:text-base line-clamp-1">
                            {employeeName}
                        </p>
                    </div>
                    <div className="flex items-center shrink-0">
                        <HOUSE className="w-5 h-5 shrink-0 text-primary" />
                        <p className="pl-2 text-sm 2xl:text-base whitespace-nowrap">
                            {objectCount} {objectCount === 1 ? "Objekt" : "Objekte"}
                        </p>
                    </div>
                    {canEdit && (
                        <BaseButton label="Bearbeiten" icon={<SquarePen className="w-5 h-5" />} func={() => modalCon?.trigger(3)} colorway="secondary" variant="outline" classProps="h-10"/>
                    )}
                </div>
            </BaseButtonRow>
            <BaseGrowBody classProps="overflow-hidden my-2 grow min-h-0">
                <TourWeekBoard
                    tour={tour}
                    tasksByObject={tasksByObject}
                    otherTours={otherTours}
                    canEdit={canEdit}
                    onAddStop={(weekday) => {
                        setAddWeekday(weekday)
                        modalCon?.trigger(2)
                    }}
                    onRemoveStop={(stopId) => removeStop({ tourId: tour._id, stopId })}
                    onMoveUp={(stopId) => reorderStop({ tourId: tour._id, stopId, direction: -1 })}
                    onMoveDown={(stopId) => reorderStop({ tourId: tour._id, stopId, direction: 1 })}
                    onChangeWeekday={(stopId, weekday) => moveWeekday({ tourId: tour._id, stopId, weekday })}
                    onMoveToTour={(stopId, toTourId, weekday) => moveToTour({ fromTourId: tour._id, toTourId, stopId, weekday })}
                />
            </BaseGrowBody>
            <EditTourModal
                modalId={3}
                tour={tour}
                employees={employees ?? []}
                employeesLoading={employeesLoading}
                onSearchEmployee={setSearchEmployee}
                onSave={({ name, employee, active }) => {
                    updateTour({ id: tour._id, name, active, employee: employee?._id ?? null })
                }}
            />
            <AddStopModal
                modalId={2}
                defaultWeekday={addWeekday}
                planStops={planStops}
                onSave={({ object, weekday, adHoc }) => {
                    addStop({
                        tourId: tour._id,
                        object,
                        weekday,
                        extra: adHoc ? { isAdHoc: true, adHocTask: adHoc } : undefined,
                    })
                }}
            />
        </BasePage>
    )
}
